// Container-state controller shared by the standardised resource list pages.
// Owns the fiddly, identical bits of every XManagement container: the search
// input + debounce + URL sync, the SortingState <-> {sort, direction} bridge,
// the API params, the SSR initialData one-shot gate, and the detail-sheet /
// delete-dialog selection state. The container stays thin and wires the
// resource-specific query, detail sheet and create dialog around it.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SortingState } from '@tanstack/react-table';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import type { ResourceListSearchParams, ResourceRouterSearch } from '@hooks/resourceListPage';
import { searchQueryParam } from '@/react/utils/searchQuery';

// Params accepted by the standardised list endpoints. sortBy / sortDirection
// are only present when an active sort is set, matching the per-resource
// containers exactly.
export interface ResourceListApiParams {
  q?: string;
  page: number;
  perPage: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface ResourceListPageController<TInitial> {
  // Search input (typing UX), debounced into the URL.
  searchInput: string;
  setSearchInput: (value: string) => void;
  // Controlled table sorting, bridged to the URL sort / direction.
  sorting: SortingState;
  handleSortingChange: (next: SortingState) => void;
  // Params for the list query, and the initialData to seed it with (only on the
  // default first view, then undefined so React Query owns the cache).
  apiParams: ResourceListApiParams;
  initialDataForQuery: TInitial | undefined;
  // Detail-sheet selection.
  selectedId: number | null;
  setSelectedId: (id: number | null) => void;
  selectRow: (row: { id: number }) => void;
  handleSheetOpenChange: (open: boolean) => void;
  // Delete confirmation.
  deleteId: number | null;
  requestDelete: (id: number) => void;
  clearDelete: () => void;
}

export interface ResourceListPageControllerOptions<TSort extends string> {
  // Predicate deciding whether SSR initialData may seed the first query.
  // Defaults to the unfiltered first page (q empty, page 1, no sort). Resources
  // with a stricter rule (e.g. also requiring the default page size) pass their
  // own — this preserves each resource's exact seeding behaviour.
  isInitialDataEligible?: (search: ResourceListSearchParams<TSort>) => boolean;
}

export function useResourceListPageController<TSort extends string, TInitial = unknown>(
  router: ResourceRouterSearch<TSort>,
  initialData?: TInitial,
  options?: ResourceListPageControllerOptions<TSort>,
): ResourceListPageController<TInitial> {
  const { search } = router;

  // Local search input for typing UX. Debounce into the URL; re-sync from the
  // URL when navigation changes it externally (popstate / link nav).
  const [searchInput, setSearchInput] = useState(search.q);
  const debouncedQuery = useDebouncedValue(searchInput);

  // Track the last value we wrote so the URL->input sync can tell "external
  // nav" (sync the input) from "our own debounce write" (don't clobber the
  // user's in-flight keystrokes).
  const lastNavigatedQRef = useRef(search.q);

  useEffect(() => {
    if (lastNavigatedQRef.current !== search.q) {
      setSearchInput(search.q);
    }
  }, [search.q]);

  useEffect(() => {
    // Also gate on debouncedQuery === searchInput: if the user has typed newer
    // characters than the debounce has settled, don't write a stale value.
    if (debouncedQuery !== search.q && debouncedQuery === searchInput) {
      lastNavigatedQRef.current = debouncedQuery;
      router.setQuery(debouncedQuery);
    }
  }, [debouncedQuery, search.q, searchInput, router]);

  // SortingState <-> {sort, direction}. TanStack uses an array; we only ever
  // sort one column at a time, so the first entry wins.
  const sorting: SortingState = useMemo(() => {
    if (!search.sort || !search.direction) return [];
    return [{ id: search.sort, desc: search.direction === 'desc' }];
  }, [search.sort, search.direction]);

  const handleSortingChange = useCallback(
    (next: SortingState) => {
      if (next.length === 0) {
        router.setSort('', '');
        return;
      }
      const { id, desc } = next[0];
      router.setSort(id as TSort, desc ? 'desc' : 'asc');
    },
    [router],
  );

  const apiParams: ResourceListApiParams = {
    q: searchQueryParam(search.q),
    page: search.page,
    perPage: search.perPage,
    ...(search.sort && search.direction
      ? { sortBy: search.sort, sortDirection: search.direction as 'asc' | 'desc' }
      : {}),
  };

  // Only seed from SSR initialData on the default first view to avoid a loading
  // flash. After mount, React Query owns the cache and refetches as needed.
  const initialDataRef = useRef(initialData);
  useEffect(() => {
    initialDataRef.current = undefined;
  }, []);
  const isDefaultQuery = options?.isInitialDataEligible
    ? options.isInitialDataEligible(search)
    : !search.q && search.page === 1 && !search.sort;
  const initialDataForQuery = isDefaultQuery ? initialDataRef.current : undefined;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selectRow = useCallback((row: { id: number }) => setSelectedId(row.id), []);
  const handleSheetOpenChange = useCallback((open: boolean) => {
    if (!open) setSelectedId(null);
  }, []);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const requestDelete = useCallback((id: number) => setDeleteId(id), []);
  const clearDelete = useCallback(() => setDeleteId(null), []);

  return {
    searchInput,
    setSearchInput,
    sorting,
    handleSortingChange,
    apiParams,
    initialDataForQuery,
    selectedId,
    setSelectedId,
    selectRow,
    handleSheetOpenChange,
    deleteId,
    requestDelete,
    clearDelete,
  };
}
